import {
    MutationFunction,
    useMutation,
    UseMutationResult,
} from '@tanstack/react-query';
import {useMemo, useState} from 'react';

import useMutationButtonProps, {
    UseMutationButtonReturnType,
} from './mutation-button-state';
import {MutationRequest} from './types';

export type UseTrackedMutationResult<
    TData = unknown,
    TError = unknown,
    TVariables = void,
    TContext = unknown,
> = {
    buttonProps: UseMutationButtonReturnType;
} & UseMutationResult<TData, TError, AdditionalProps & TVariables, TContext>;

type AdditionalProps = {
    requestId: string;
};

const useTrackedMutation = <
    TData = unknown,
    TError = unknown,
    TVariables extends object = Record<string, string>,
>(
    mutationFunction: MutationFunction<TData, TVariables>,
): UseTrackedMutationResult<TData, TError, TVariables> => {
    const [mutationRequests, setPendingMutationRequests] = useState<
        Record<string, MutationRequest>
    >({});

    const buttonProps = useMutationButtonProps(mutationRequests);

    const mutate = useMutation<TData, TError, AdditionalProps & TVariables>({
        mutationFn: mutationFunction,
        onError: (_, variables) => {
            if ('requestId' in variables) {
                const requestId = variables.requestId as string;
                setPendingMutationRequests((prev) => ({
                    ...prev,
                    [requestId]: 'error',
                }));

                return {requestId: variables.requestId};
            }
            throw new Error('requestId is not in variables');
        },
        onMutate: async (variables) => {
            if ('requestId' in variables) {
                const requestId = variables.requestId as string;
                setPendingMutationRequests((prev) => ({
                    ...prev,
                    [requestId]: 'pending',
                }));

                return {requestId: variables.requestId};
            }
            throw new Error('requestId is not in variables');
        },
        onSettled: (_, error, variables) => {
            if (error) return;

            if ('requestId' in variables) {
                const requestId = variables.requestId as string;
                setPendingMutationRequests((prev) => {
                    const newPendingMutationRequests = {...prev};
                    delete newPendingMutationRequests[requestId];

                    return newPendingMutationRequests;
                });

                return {requestId: variables.requestId};
            }
            throw new Error('requestId is not in variables');
        },
    });

    return useMemo(
        () => ({
            ...mutate,
            buttonProps,
        }),
        [buttonProps, mutate],
    );
};

export default useTrackedMutation;
